import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import { ROUTES, supportPath } from "@/routes";
import {
  Copy,
  Loader2,
  Mail,
  MoreHorizontal,
  Pencil,
  Trash2,
  UserPen,
} from "lucide-react";
import { useHasGroup } from "@/hooks/useHasGroup";
import { StageBadge } from "./components/StageBadge";
import { fetchMerchantDebugContext } from "@/application/merchants/debug_context_repository";
import { BodySection } from "@/components/ui/BodySection";
import { ViewAllLink } from "@/components/ui/ViewAllLink";
import { AuditLogSection } from "./components/AuditLogSection";
import { formatDebugBundle } from "./components/debug-bundle";
import { PlatformsCard } from "./components/PlatformsCard";
import { MobilePlatformSheet } from "./components/MobilePlatformSheet";
import { TerminalPlatformSheet } from "./components/TerminalPlatformSheet";
import { MoneyMovementSection } from "./components/MoneyMovementSection";
import { CapabilitiesCard } from "./components/CapabilitiesCard";
import { CapabilitiesSheet } from "./components/CapabilitiesSheet";
import { BillingCard } from "./components/BillingCard";
import { BillingSheet } from "./components/BillingSheet";
import { ProfileCard } from "./components/ProfileCard";
import { ProfileSheet } from "./components/ProfileSheet";
import { LifecycleSheet } from "./components/LifecycleSheet";
import { DebugLinksRow } from "./components/DebugLinksRow";
import { TrackSheet } from "./components/TrackSheet";
import { MerchantSupportTab } from "@/presentation/support/components/MerchantSupportTab";
import { TransactionsList } from "@/presentation/money/TransactionsList";
import { PayoutsList } from "@/presentation/money/PayoutsList";
import { InvoicesList } from "@/presentation/money/InvoicesList";
import { EventsView } from "@/presentation/events/EventsView";
import { ChangeLoginEmailDialog } from "./components/ChangeLoginEmailDialog";
import { EditBusinessInfoDialog } from "./components/EditBusinessInfoDialog";
import { EditPersonalInfoDialog } from "./components/EditPersonalInfoDialog";
import { PurgeMerchantDialog } from "./components/PurgeMerchantDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/base/dropdown-menu";
import { MerchantsRepository } from "@/application/merchants/merchants_repository";
import { personRecordOf } from "@/application/merchants/personal_info_edit";
import { useBreadcrumbs } from "@/presentation/layout/breadcrumbs_context";
import { connectedAccountUrl } from "@/lib/stripe-links";
import {
  parseBusinessErrors,
  parseTracks,
  type BusinessProfile,
  type MerchantLifecycle,
  type UserProfile,
} from "./types";

const client = generateClient<Schema>();

// ── Tab type ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "transactions", label: "Transactions" },
  { id: "payouts", label: "Payouts" },
  { id: "invoices", label: "Invoices" },
  { id: "events", label: "Events" },
] as const;

type ActiveTab = (typeof TABS)[number]["id"];

function isTab(value: string | null): value is ActiveTab {
  return TABS.some((t) => t.id === value);
}

// ── Stat item ─────────────────────────────────────────────────────────────────

// ── View ──────────────────────────────────────────────────────────────────────

/** Enough to see whether this merchant is a frequent caller; the rest is a click away. */
const MERCHANT_SUPPORT_PREVIEW = 5;

export function MerchantDetailView() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const isDeveloper = useHasGroup("CPOS-Developers");

  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [lifecycle, setLifecycle] = useState<MerchantLifecycle | null>(null);
  const [users, setUsers] = useState<UserProfile[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: ActiveTab = isTab(searchParams.get("tab"))
    ? (searchParams.get("tab") as ActiveTab)
    : "overview";

  const setActiveTab = useCallback(
    (tab: ActiveTab) => {
      setSearchParams(
        (params) => {
          if (tab === "overview") params.delete("tab");
          else params.set("tab", tab);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Store only the track *name* — derive the current state from the fresh
  // `lifecycle` at render time so post-retry refetches flow down to the sheet.
  // Storing a snapshot would leave stage/current-state/payout-banner stale
  // even after `loadData()` refreshes `lifecycle`.
  const [openTrackName, setOpenTrackName] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [supportCount, setSupportCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  /**
   * Fetches on demand rather than with the page: the bundle wants the device
   * and Cognito state, and that query reaches Cognito and Amplitude.
   */
  const copyDebugBundle = useCallback(async () => {
    if (!business) return;
    try {
      const context = await fetchMerchantDebugContext(business.id);
      await navigator.clipboard.writeText(
        formatDebugBundle({
          businessId: business.id,
          businessName: business.businessName ?? null,
          primaryUserId: context.authState?.userId ?? null,
          stripeConnectedAccountId: business.stripeConnectedAccountId ?? null,
          lifecycleStage: lifecycle?.overallStage ?? null,
          environment: import.meta.env.VITE_HUB_ENVIRONMENT ?? "development",
          deviceState: context.deviceState,
          authState: context.authState,
        }),
      );
      toast.success("Debug bundle copied");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not build the debug bundle",
      );
    }
  }, [business, lifecycle]);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [editInfoFor, setEditInfoFor] = useState<string | null>(null);
  const [editPersonalFor, setEditPersonalFor] = useState<string | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const repo = useMemo(() => new MerchantsRepository(), []);

  async function loadData() {
    if (!businessId) return;
    setLoading(true);
    setUsers(null);
    try {
      const [lifecycleResult, businessResult] = await Promise.all([
        client.models.MerchantLifecycle.get({ businessId }),
        client.models.BusinessProfile.get({ id: businessId }),
      ]);
      setLifecycle(lifecycleResult.data ?? null);
      setBusiness(businessResult.data ?? null);

      // The client reports failures on `errors` rather than throwing, so
      // `catch` alone would turn a failed read into "no users" — and then
      // into "Not referred" downstream. `null` means unknown, `[]` means none.
      try {
        const usersResult =
          await client.models.UserProfile.listUserProfileByBusinessId({
            businessId,
          });
        setUsers(usersResult.errors?.length ? null : (usersResult.data ?? []));
      } catch {
        setUsers(null);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useBreadcrumbs([
    { label: "Merchants", to: ROUTES.MERCHANTS },
    ...(business
      ? [{ label: business.businessName ?? businessId ?? "Merchant" }]
      : []),
  ]);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  // ── Business not found ─────────────────────────────────────────────────────

  if (!business) {
    return (
      <div className="text-muted-foreground text-center">
        Merchant not found.{" "}
        <Link
          to={ROUTES.MERCHANTS}
          className="text-blue-600 hover:text-blue-800"
        >
          Back to merchants
        </Link>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  const stripeErrors = parseBusinessErrors(business.errors)?.stripe ?? null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-card flex min-h-screen flex-col">
      {/* ── Header bar ──────────────────────────────────────────────────────── */}
      <div className="bg-card @container sticky top-0 z-10 border-b">
        <div className="flex items-center gap-4 px-6 pb-3">
          {/* Identity */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="text-foreground truncate text-3xl font-bold">
              {business.businessName ?? businessId}
            </span>
            {lifecycle?.overallStage && (
              <span className="shrink-0">
                <StageBadge stage={lifecycle.overallStage} />
              </span>
            )}
            {business.stripeConnectedAccountId && (
              <a
                href={connectedAccountUrl(business.stripeConnectedAccountId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-subtle-foreground hidden max-w-[14rem] truncate font-mono text-xs hover:text-blue-600 hover:underline @5xl:inline-block"
              >
                {business.stripeConnectedAccountId}
              </a>
            )}
          </div>

          {/* Admin actions */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="Merchant actions"
                className="border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-md border"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => void copyDebugBundle()}>
                <Copy />
                Copy debug bundle
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setChangeEmailOpen(true)}>
                <Mail />
                Change login email
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setEditInfoFor(business.id)}>
                <Pencil />
                Edit business info
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={personRecordOf(business) === null}
                onSelect={() => setEditPersonalFor(business.id)}
              >
                <UserPen />
                Edit personal info
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={!isDeveloper}
                onSelect={() => setPurgeOpen(true)}
              >
                <Trash2 />
                <div className="flex flex-col">
                  <span>Delete permanently</span>
                  {!isDeveloper && (
                    <span className="text-xs opacity-70">
                      Requires the CPOS-Developers role
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* ── Debug links ─────────────────────────────────────────────────── */}
        <DebugLinksRow
          businessId={business.id}
          primaryUserId={
            users?.find((u) => u.userId === business.userIds?.[0])?.userId ??
            users?.[0]?.userId
          }
          stripeConnectedAccountId={business.stripeConnectedAccountId}
        />

        {/* ── Tab bar ─────────────────────────────────────────────────────── */}
        <div className="flex border-t">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                "px-6 py-3 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ───────────────────────────────────────────────────────── */}
      <div className="flex-1 p-6">
        {activeTab === "overview" && (
          <div
            className="grid gap-6"
            style={{ gridTemplateColumns: "2fr 1fr" }}
          >
            {/* Main column */}
            <div className="space-y-10">
              {businessId && <MoneyMovementSection businessId={businessId} />}

              {businessId && (
                <BodySection
                  title="Support Tickets"
                  footer={
                    // Nothing to view when they have never raised one.
                    supportCount > 0 ? (
                      <ViewAllLink to={supportPath(businessId)}>
                        View all support tickets
                      </ViewAllLink>
                    ) : null
                  }
                >
                  <MerchantSupportTab
                    businessId={businessId}
                    limit={MERCHANT_SUPPORT_PREVIEW}
                    onCountChange={setSupportCount}
                  />
                </BodySection>
              )}

              {businessId && <AuditLogSection businessId={businessId} />}

              <LifecycleSheet
                lifecycle={lifecycle}
                business={business}
                onRefresh={loadData}
                onTrackClick={(name) => setOpenTrackName(name)}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <PlatformsCard
                businessId={business.id}
                onOpenMobile={() => setMobileOpen(true)}
                onOpenTerminal={() => setTerminalOpen(true)}
              />
              <CapabilitiesCard
                business={business}
                stripeErrors={stripeErrors}
                onViewAll={() => setCapabilitiesOpen(true)}
              />
              <BillingCard
                businessId={business.id}
                onViewAll={() => setBillingOpen(true)}
              />
              <ProfileCard
                business={business}
                onViewAll={() => setProfileOpen(true)}
              />
            </div>
          </div>
        )}

        {activeTab === "transactions" && businessId && (
          <TransactionsList businessId={businessId} />
        )}

        {activeTab === "payouts" && businessId && (
          <PayoutsList businessId={businessId} />
        )}

        {activeTab === "invoices" && businessId && (
          <InvoicesList businessId={businessId} />
        )}

        {activeTab === "events" &&
          (business?.stripeConnectedAccountId ? (
            <EventsView
              scopedMode={{
                source: "connected",
                account: business.stripeConnectedAccountId,
              }}
            />
          ) : (
            <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
              <p className="text-sm">
                This merchant isn&apos;t connected to Stripe yet.
              </p>
            </div>
          ))}
      </div>

      {/* Track detail sheet (fixed overlay, outside tab content). Derive the
          track state from the current `lifecycle` on every render so the sheet
          stays in sync with refetches after a retry. */}
      {openTrackName &&
        lifecycle &&
        (() => {
          const tracks = parseTracks(lifecycle.tracks);
          // eslint-disable-next-line security/detect-object-injection -- openTrackName is only ever set to a key from the same tracks map.
          const trackState = tracks[openTrackName];
          if (!trackState) return null;
          return (
            <TrackSheet
              trackName={openTrackName}
              trackState={trackState}
              lifecycle={lifecycle}
              onClose={() => setOpenTrackName(null)}
              onLifecycleRefresh={() => {
                void loadData();
              }}
            />
          );
        })()}

      <MobilePlatformSheet
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        businessId={business.id}
      />

      <TerminalPlatformSheet
        open={terminalOpen}
        onOpenChange={setTerminalOpen}
        businessId={business.id}
        terminalName={null}
      />

      <CapabilitiesSheet
        open={capabilitiesOpen}
        onOpenChange={setCapabilitiesOpen}
        business={business}
        stripeErrors={stripeErrors}
      />

      <BillingSheet
        open={billingOpen}
        onOpenChange={setBillingOpen}
        businessId={business.id}
      />

      <ProfileSheet
        open={profileOpen}
        onOpenChange={setProfileOpen}
        business={business}
        users={users}
        lifecycle={lifecycle}
        stripeErrors={stripeErrors}
        onRefresh={() => void loadData()}
        onEditBusinessInfo={() => setEditInfoFor(business.id)}
        onEditPersonalInfo={() => setEditPersonalFor(business.id)}
      />

      <ChangeLoginEmailDialog
        businessProfileId={business.id}
        currentEmail={
          users && business.userIds && business.userIds[0]
            ? (users.find((u) => u.userId === business.userIds?.[0])?.email ??
              users[0]?.email)
            : null
        }
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
        onEmailChanged={() => void loadData()}
        repo={repo}
      />

      {editInfoFor === business.id && (
        <EditBusinessInfoDialog
          business={business}
          repo={repo}
          onClose={(saved) => {
            setEditInfoFor(null);
            if (saved) void loadData();
          }}
        />
      )}

      {editPersonalFor === business.id && (
        <EditPersonalInfoDialog
          business={business}
          repo={repo}
          onClose={(saved) => {
            setEditPersonalFor(null);
            if (saved) void loadData();
          }}
        />
      )}

      {isDeveloper && (
        <PurgeMerchantDialog
          businessId={business.id}
          businessName={business.businessName ?? null}
          open={purgeOpen}
          onOpenChange={setPurgeOpen}
          onPurged={() => navigate(ROUTES.MERCHANTS)}
          repo={repo}
        />
      )}
    </div>
  );
}
